import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Pagination,
  Grid,
  CircularProgress,
  Switch,
  FormControlLabel,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import { hasPermission } from '../../../../utils/permissionUtils';
import { formatEnumLabel } from '../../../../constants/hrm';
import {
  useDepartments,
  useCreateDepartment,
  useUpdateDepartment,
  useDeleteDepartment,
  useEmployees,
} from '../../../../hooks/useHRM';

const initialFormData = {
  name: '',
  code: '',
  description: '',
  cost_center: '',
  parent_department_id: '',
  head_employee_id: '',
  is_active: true,
};

const DepartmentList = () => {
  const { chatbotId } = useParams();

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingDepartment, setEditingDepartment] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [formErrors, setFormErrors] = useState({});

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [departmentToDelete, setDepartmentToDelete] = useState(null);

  const [canView, setCanView] = useState(false);
  const [canModify, setCanModify] = useState(false);

  // React Query hooks
  const filters = {
    page,
    per_page: 20,
    ...(search && { search }),
  };

  const { data: departmentData, isLoading: loading } = useDepartments(chatbotId, filters);
  const { data: allDepartmentData } = useDepartments(chatbotId, { per_page: 1000 });
  const { data: employeeData } = useEmployees(chatbotId, { per_page: 1000 });

  const departments = departmentData?.data || [];
  const pagination = departmentData?.pagination || { total: 0, pages: 1, per_page: 20 };
  const allDepartments = allDepartmentData?.data || [];
  const employees = employeeData?.data || [];

  const createDepartment = useCreateDepartment(chatbotId);
  const updateDepartment = useUpdateDepartment(chatbotId);
  const deleteDepartment = useDeleteDepartment(chatbotId);

  const saving = createDepartment.isPending || updateDepartment.isPending;
  const deleting = deleteDepartment.isPending;

  useEffect(() => {
    const checkPermissions = async () => {
      const view = await hasPermission('hrm', 'view');
      const modify = await hasPermission('hrm', 'add|edit');
      setCanView(view);
      setCanModify(modify);
    };
    checkPermissions();
  }, []);

  const handleOpenCreate = () => {
    setEditingDepartment(null);
    setFormData(initialFormData);
    setFormErrors({});
    setDialogOpen(true);
  };

  const handleOpenEdit = (department) => {
    setEditingDepartment(department);
    setFormData({
      name: department.name || '',
      code: department.code || '',
      description: department.description || '',
      cost_center: department.cost_center || '',
      parent_department_id: department.parent_department_id || '',
      head_employee_id: department.head_employee_id || '',
      is_active: department.is_active !== undefined ? department.is_active : true,
    });
    setFormErrors({});
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingDepartment(null);
    setFormData(initialFormData);
    setFormErrors({});
  };

  const handleFormChange = (field) => (e) => {
    const value = field === 'is_active' ? e.target.checked : e.target.value;
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) errors.name = 'Department name is required';
    if (!formData.code.trim()) errors.code = 'Department code is required';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    try {
      const payload = { ...formData };

      // Convert empty strings to null
      if (!payload.description) payload.description = null;
      if (!payload.cost_center) payload.cost_center = null;
      if (!payload.parent_department_id) payload.parent_department_id = null;
      if (!payload.head_employee_id) payload.head_employee_id = null;

      if (editingDepartment) {
        await updateDepartment.mutateAsync({ id: editingDepartment.id, ...payload });
      } else {
        await createDepartment.mutateAsync(payload);
      }
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving department:', error);
      if (error.response?.data?.message) {
        setFormErrors({ general: error.response.data.message });
      }
    }
  };

  const handleOpenDelete = (department) => {
    setDepartmentToDelete(department);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!departmentToDelete) return;
    try {
      await deleteDepartment.mutateAsync(departmentToDelete.id);
      setDeleteDialogOpen(false);
      setDepartmentToDelete(null);
    } catch (error) {
      console.error('Error deleting department:', error);
    }
  };

  const getParentDepartmentName = (parentId) => {
    const parent = allDepartments.find((d) => d.id === parentId);
    return parent ? parent.name : '-';
  };

  if (!canView) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" color="error">
          You do not have permission to view departments.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <Typography variant="h4" fontWeight="bold" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
          Departments
        </Typography>
        {canModify && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreate} size="small">
            Add Department
          </Button>
        )}
      </Box>

      {/* Search */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search departments..."
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          InputProps={{
            startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
          }}
        />
      </Paper>

      {/* Department Table */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
            <Table sx={{ minWidth: 700 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Code</TableCell>
                  <TableCell>Parent Department</TableCell>
                  <TableCell>Head Employee</TableCell>
                  <TableCell align="center">Employee Count</TableCell>
                  <TableCell align="center">Position Count</TableCell>
                  <TableCell align="center">Status</TableCell>
                  {canModify && <TableCell align="right">Actions</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {departments.length > 0 ? (
                  departments.map((department) => (
                    <TableRow key={department.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {department.name}
                        </Typography>
                        {department.description && (
                          <Typography variant="caption" color="textSecondary" display="block">
                            {department.description}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip label={department.code} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>
                        {department.parent_department_id
                          ? department.parent_department_name || getParentDepartmentName(department.parent_department_id)
                          : '-'}
                      </TableCell>
                      <TableCell>
                        {department.head_employee_name || '-'}
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={department.employee_count ?? 0}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={department.position_count ?? 0}
                          size="small"
                          color="secondary"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={department.is_active ? 'Active' : 'Inactive'}
                          color={department.is_active ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      {canModify && (
                        <TableCell align="right">
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => handleOpenEdit(department)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleOpenDelete(department)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={canModify ? 8 : 7} align="center" sx={{ py: 4 }}>
                      <Typography color="textSecondary">No departments found</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {pagination.pages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                count={pagination.pages}
                page={page}
                onChange={(e, value) => setPage(value)}
                color="primary"
              />
            </Box>
          )}
        </>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingDepartment ? 'Edit Department' : 'Create Department'}
        </DialogTitle>
        <DialogContent>
          {formErrors.general && (
            <Typography color="error" sx={{ mb: 2 }}>
              {formErrors.general}
            </Typography>
          )}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Department Name"
                value={formData.name}
                onChange={handleFormChange('name')}
                error={!!formErrors.name}
                helperText={formErrors.name}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Department Code"
                value={formData.code}
                onChange={handleFormChange('code')}
                error={!!formErrors.code}
                helperText={formErrors.code}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={handleFormChange('description')}
                multiline
                rows={3}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Cost Center"
                value={formData.cost_center}
                onChange={handleFormChange('cost_center')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Parent Department</InputLabel>
                <Select
                  value={formData.parent_department_id}
                  onChange={handleFormChange('parent_department_id')}
                  label="Parent Department"
                >
                  <MenuItem value="">None</MenuItem>
                  {allDepartments
                    .filter((d) => !editingDepartment || d.id !== editingDepartment.id)
                    .map((dept) => (
                      <MenuItem key={dept.id} value={dept.id}>
                        {dept.name}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Head Employee</InputLabel>
                <Select
                  value={formData.head_employee_id}
                  onChange={handleFormChange('head_employee_id')}
                  label="Head Employee"
                >
                  <MenuItem value="">None</MenuItem>
                  {employees.map((emp) => (
                    <MenuItem key={emp.id} value={emp.id}>
                      {emp.first_name} {emp.last_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={handleFormChange('is_active')}
                  />
                }
                label="Active"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={24} /> : editingDepartment ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Department</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the department{' '}
            <strong>{departmentToDelete?.name}</strong>? This action cannot be undone.
          </Typography>
          {(departmentToDelete?.employee_count > 0 || departmentToDelete?.position_count > 0) && (
            <Typography color="error" sx={{ mt: 1 }}>
              Warning: This department has {departmentToDelete.employee_count || 0} employee(s) and{' '}
              {departmentToDelete.position_count || 0} position(s) associated with it.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDelete}
            disabled={deleting}
          >
            {deleting ? <CircularProgress size={24} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DepartmentList;
